import {Loader} from "components/index";
import {notifySlack} from "helpers/SlackNotifier";
import React, {ErrorInfo, ReactNode} from "react";
import {RouteComponentProps, withRouter} from "react-router-dom";
import StackTrace from "stacktrace-js";
import store from "store";
import {TUserReducerState} from "store/user/types";
import {clearCaches, isChunkLoadError} from "utils/lazyWithRetry";

// --- Persistent RateLimiter ---
class RateLimiter {
	constructor(private key: string, private maxCalls: number, private windowMs: number) {}

	try(): boolean {
		const now = Date.now();
		const raw = localStorage.getItem(this.key) || "[]";
		const timestamps: number[] = JSON.parse(raw);

		// Keep only those within the window
		const valid = timestamps.filter((ts) => now - ts < this.windowMs);

		if (valid.length < this.maxCalls) {
			valid.push(now);
			localStorage.setItem(this.key, JSON.stringify(valid));

			return true;
		}

		// update pruned list
		localStorage.setItem(this.key, JSON.stringify(valid));

		return false;
	}
}

// 5 reports per 10 mins by the user, persisted under this storage key
const slackRateLimiter = new RateLimiter("ErrorBoundarySlackTimestamps", 5, 10 * 60 * 1000);

// Key to track chunk reload attempts
const CHUNK_RELOAD_KEY = "chunk_error_reload_attempted";

interface ErrorBoundaryProps {
	children: ReactNode;
}

interface State {
	hasError: boolean;
	isReloading: boolean;
}

type props = ErrorBoundaryProps & RouteComponentProps;

class ErrorBoundary extends React.Component<props, State> {
	state: State = {hasError: false, isReloading: false};

	static getDerivedStateFromError() {
		return {hasError: true};
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		// Handle ChunkLoadError specially - auto-reload instead of error page
		if (isChunkLoadError(error)) {
			this.handleChunkLoadError(error);

			return;
		}

		const state = store.getState();
		const userState = state.user as TUserReducerState;
		const userName = userState?.user?.firstName + " " + userState?.user?.lastName || "unknown";
		const organisationName = userState?.user?.organisation?.name || "unknown org";
		const webhookUrl = process.env.REACT_APP_SLACK_WEBHOOK_URL;
		const isProd = process.env.NODE_ENV === "production";

		if (webhookUrl && isProd && slackRateLimiter.try()) {
			console.warn("Sending Slack notification");

			const fallbackMessage = `
            *Error:* ${error.toString()}
            *User Name:* ${userName}
            *Org Name:* ${organisationName}
            *Component Stack:*
            \`\`\`
            ${info.componentStack}
            \`\`\`
        `;

			StackTrace.fromError(error)
				.then((stackframes) => {
					const stringifiedStack = stackframes
						.map((sf) => {
							return `    at ${sf.functionName || "<anonymous>"} (${sf.fileName}:${
								sf.lineNumber
							}:${sf.columnNumber})`;
						})
						.join("\n");

					const message = `
            *Error:* ${error.toString()}
            *User Name:* ${userName}
            *Org Name:* ${organisationName}
            *Stack Trace:*
            \`\`\`
            ${stringifiedStack}
            \`\`\`
            *Component Stack:*
            \`\`\`
            ${info.componentStack}
            \`\`\`
        `;

					return notifySlack(message);
				})
				.catch((err) => {
					console.error("Stack trace generation failed:", err);

					return notifySlack(fallbackMessage);
				})
				.finally(() => this.redirectOnError(error, info));
		} else {
			if (!slackRateLimiter.try()) {
				console.warn("Slack notification rate limit exceeded—skipping report");
			}
			this.redirectOnError(error, info);
		}
	}

	private async handleChunkLoadError(error: Error) {
		const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);

		if (!hasReloaded) {
			console.warn(
				"[ErrorBoundary] ChunkLoadError detected, clearing caches and reloading...",
			);

			// Mark that we've attempted a reload
			sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");

			// Update state to show reloading message
			this.setState({isReloading: true});

			// Clear caches
			await clearCaches();

			// Force hard reload
			window.location.reload();
		} else {
			// We've already tried reloading - this is a persistent error
			console.error(
				"[ErrorBoundary] ChunkLoadError persists after reload, redirecting to error page",
			);
			sessionStorage.removeItem(CHUNK_RELOAD_KEY);

			// Send notification for persistent chunk errors
			const state = store.getState();
			const userState = state.user as TUserReducerState;
			const userName =
				userState?.user?.firstName + " " + userState?.user?.lastName || "unknown";
			const organisationName = userState?.user?.organisation?.name || "unknown org";
			const webhookUrl = process.env.REACT_APP_SLACK_WEBHOOK_URL;
			const isProd = process.env.NODE_ENV === "production";

			if (webhookUrl && isProd && slackRateLimiter.try()) {
				const message = `
				*⚠️ Persistent ChunkLoadError:* ${error.toString()}
				*User Name:* ${userName}
				*Org Name:* ${organisationName}
				*Note:* User has already tried reloading the page.
				`;
				notifySlack(message);
			}

			this.setState({hasError: false, isReloading: false});

			if (process.env.NODE_ENV === "production") {
				this.props.history.push("/unknown-error");
			}
		}
	}

	private redirectOnError(error: Error, info: ErrorInfo) {
		console.error(error);
		console.error(info);
		this.setState({hasError: false, isReloading: false});
		// window.location.href = "/unknown-error";

		if (process.env.NODE_ENV === "production") {
			this.props.history.push("/unknown-error");
		}
	}

	render() {
		if (this.state.isReloading) {
			return (
				<h1>
					Updating application… <Loader height={16} width={16} type="ThreeDots" />
				</h1>
			);
		}

		if (this.state.hasError) {
			return (
				<h1>
					Sending Error Report… <Loader height={16} width={16} type="ThreeDots" />
				</h1>
			);
		}

		return this.props.children;
	}
}

export default withRouter(ErrorBoundary);
